import React from 'react'
import { observer } from "mobx-react";
import { ManagerDto, ReviewEmployeeAccountDto } from 'src/stores/models/qarFormModels';
import { Paper, TextField, Button, Tooltip, Typography, Box } from '@material-ui/core';
import { EmployeeReviewActions } from 'src/stores/models/enums';
import { Autocomplete } from '@material-ui/lab';
import { useStores } from 'src/stores/rootStore';
import { computed } from 'mobx';
import QARFormHelper from './helper';
import { ReviewEmployeeDto } from '../../stores/models/qarFormModels';
import Grid from '@material-ui/core/Grid';
import SupervisorAccountOutlinedIcon from '@material-ui/icons/SupervisorAccountOutlined';
import GroupAddSharpIcon from '@material-ui/icons/GroupAddSharp';

export const QARFormDelegate = observer(({ employee, accounts, accountsChanges }:
    { employee: ReviewEmployeeDto, accounts: ReviewEmployeeAccountDto[], accountsChanges: ReviewEmployeeAccountDto[] }) => {

    const { qarFormStore, uiStateStore } = useStores();
    // caching point
    // computed value outside of store        
    const delegatedFromManager = computed(() => {
        return accounts.find(x => x.delegatedFromManager)?.delegatedFromManager;
    }).get()

    const getOptions = (employeeId: string) => {
        const currentReviewingManager = qarFormStore.manager;
        const currentlySelectedManager = qarFormStore.selectedManagerId;

        if (delegatedFromManager) {
            // you can only handle delegation back to the source manager
            return qarFormStore.allManagers!.filter(x => x.id === delegatedFromManager.id)
        }

        // don't delegate your employees to youself please
        return qarFormStore.allManagers!.filter(x => x.id !== currentReviewingManager!.id && x.id !== currentlySelectedManager && x.id !== employeeId)
    }

    const handleOnChange = (_event: React.ChangeEvent<{}>, value: ManagerDto | null) => {
      qarFormStore.delegationsInProgress = {...qarFormStore.delegationsInProgress, ...{[employee.id]: value!}};
    }

    const cancelDelegationHandler = () => {
      uiStateStore.setModalOpen(false);
    };

    const delegateBackHandler = () => {
      const originalManager = getOptions(employee.id)[0];
      accountsChanges.forEach((account: ReviewEmployeeAccountDto) => {
        account.action.name = EmployeeReviewActions.DelegatedTo;
        account.action.delegatedTo = originalManager;
      })
      qarFormStore.delegationsInProgress = {...qarFormStore.delegationsInProgress, ...{[employee.id]:originalManager}};
      uiStateStore.setModalOpen(false);
    }

    const delegateHandler = () => {
      if (qarFormStore.delegationsInProgress[employee.id] !== undefined &&
          qarFormStore.delegationsInProgress[employee.id] !== null) {
        accountsChanges.forEach((account: ReviewEmployeeAccountDto) => {
            account.action.name = EmployeeReviewActions.DelegatedTo;
            account.action.delegatedTo = qarFormStore.delegationsInProgress[employee.id]
        })
    } else {
        accountsChanges.forEach((account: ReviewEmployeeAccountDto) => {
            account.action.name = EmployeeReviewActions.MissingAction;
            account.action.delegatedTo = null;
        })
    }

      uiStateStore.setModalOpen(false);
    };

    const setModalContent = () => {
      const modalContent = renderFormControl();
      uiStateStore.setModalContent(modalContent);
      uiStateStore.setModalOpen(true);
    };

    const renderDelegationButton = () => {
      const shouldDisableControl = accountsChanges.some((x) =>
          x.action.name !== EmployeeReviewActions.MissingAction &&
          x.action.name !== EmployeeReviewActions.DelegatedTo
      );

      const isBeingDelegated = qarFormStore.employeesChanges!.find(x => x.id === employee.id)?.accounts[0].action.name === EmployeeReviewActions.DelegatedTo;
      const isInitiallyDelegated = qarFormStore.employees!.find(x => x.id === employee.id)?.accounts[0].action.name === EmployeeReviewActions.DelegatedTo;

      if (delegatedFromManager) {
        if (isBeingDelegated && !isInitiallyDelegated) {
          return (
            <>
              <Tooltip 
                title={QARFormHelper.renderTooltipMessage(
                  `When the form is saved, this employee will be delegated to ${
                    qarFormStore.delegationsInProgress[employee.id]?.fullName}`)}
                placement="top"
                arrow
              >
                <span>
                  <Button disabled={shouldDisableControl} onClick={(_) => setModalContent()}>
                    <GroupAddSharpIcon fontSize="large" htmlColor="#2DBF64"></GroupAddSharpIcon>
                  </Button>
                </span>
              </Tooltip>
            </>
          );
        }
        
        return (
          <>
            <Tooltip
              title={QARFormHelper.renderTooltipMessage(
                `This account has been delegated to you from ${delegatedFromManager.fullNames}`)}
              placement="top"
              arrow
            >
              <span>
                <Button disabled={shouldDisableControl} onClick={(_) => setModalContent()}>
                  <GroupAddSharpIcon fontSize="large"></GroupAddSharpIcon>
                </Button>
              </span>
            </Tooltip>
          </>
        );
      }
      
      if (isBeingDelegated && !isInitiallyDelegated) {
        return (
          <>
            <Tooltip
              title={QARFormHelper.renderTooltipMessage(
                `When the form is saved, this employee will be delegated to ${
                  qarFormStore.delegationsInProgress[employee.id]?.fullName}`)}
              placement="top"
              arrow
            >
              <span>
                <Button disabled={shouldDisableControl} onClick={(_) => setModalContent()}>
                  <SupervisorAccountOutlinedIcon fontSize="large" htmlColor="#2DBF64"></SupervisorAccountOutlinedIcon>
                </Button>
              </span>
            </Tooltip>
          </>
        );
      }

      return (
        <>
          <Tooltip title={QARFormHelper.renderTooltipMessage(`Update manager`)} placement="top" arrow>
            <span>
              <Button disabled={shouldDisableControl} onClick={(_) => setModalContent()}>
                <SupervisorAccountOutlinedIcon fontSize="large"></SupervisorAccountOutlinedIcon>
              </Button>
            </span>
          </Tooltip>
        </>
      );
    };

    const renderFormControl = () => {
      const hasDelegatedAccounts = QARFormHelper.accountDelegatedTo(accounts);
      if (hasDelegatedAccounts) {
        // render nothing
        return <></>;
      }

      if (delegatedFromManager) {
        return (
          <>
            <Paper className="qar-form-employee-delegate-wrapper delegate" square>
              <p>If you are not the responsible manager to review this employee,</p>
              <p>return it to the original manager:</p>
              <h6 style={{marginLeft:"10px"}}>{delegatedFromManager.names}</h6>
            </Paper>
            <Grid container direction="row" justifyContent="flex-end" alignItems="center" style={{marginTop:"5px"}}>
              <Button onClick={(_) => cancelDelegationHandler()} color="default">Cancel</Button>
              <Button onClick={(_) => delegateBackHandler()} color="primary">Ok</Button>
            </Grid>
          </>
        );
      }

      let value = accountsChanges.find((x) => x.action?.delegatedTo)?.action.delegatedTo;

      return (
        <>
              <p>If you believe that you are not the responsible manager for this employee,</p>
              <p> please specify another manager to complete the review below:</p>
              <Paper className="qar-form-employee-delegate-wrapper delegate" square>
                <Autocomplete
                  value={value}
                  fullWidth={true}
                  options={getOptions(employee.id)}
                  getOptionLabel={(option: ManagerDto) => option.fullName}
                  onChange={handleOnChange}
                  style={{
                    textAlign: "center",
                    width: "100%",
                    paddingLeft: "0%",
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: { textAlign: "left", margin: "0 10px" },
                        className: "default-mui qar-form-employee-delegate",
                      }}
                      // label="Select manager"
                    />
                  )}
                />
              </Paper>
              <Grid container justifyContent="flex-end" alignItems="center" style={{marginTop:"10px"}}>
                  <Button onClick={(_) => cancelDelegationHandler()} color="default">Cancel</Button>
                  <Button onClick={(_) => delegateHandler()} color="primary">Ok</Button>
              </Grid>
        </>
      );
    };

    return (
        <>
            {renderDelegationButton()}
        </>
    );
});